import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Select } from 'antd';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Icon, Link, Input, Button, InfoTooltip } from 'ui';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { searchOperators } from '../../utils/app-configs';
import { determineOperatorAndValue } from '../../utils/helpers';

const Title = styled.div`
  font-weight: 400;
  font-size: 16px;
`;

export const Action = styled(Button)`
  margin-top: 20px;
  text-transform: uppercase;
`;

const Subtitle = styled.div`
  font-weight: 400;
  font-size: 14px;
  text-transform: capitalize;
`;

const HideLink = styled(Link)`
  font-size: 12px;
  text-decoration: underline;
  margin-left: 23px;
  margin-top: 10px;
`;

const HideIcon = styled(Icon)`
  margin-left: 2px;
`;

const StyledInput = styled(Input)`
  ${p => p.isColumnFilter ?
    `width: ${p.withOperator ? '50%' : '100%'}  !important;
    height: ${p.withOperator ? '34' : '32'}px;`
    :
    `width: ${p.withOperator ? '70% ' : '90%'} !important;
    height: ${p.withOperator ? '34' : '32'}px;`
  }
  input {
    height: 30px;
    margin-top: -4px;
  }
`;

const StyledDiv = styled.div`
  font-size: 16px;
  color: rgba(0, 0, 0, 0.65);
`;

const StyledWrapperCategory = styled.div`
  margin-bottom: 10px;
`;

const StyledCheckbox = styled(Checkbox)`
  font-size: 16px;
`;

const StyledSelect = styled(Select)`
  vertical-align: top;
  width: ${p => p.isColumnFilter ? '50%' : '20%'};
  font-size: 14px !important;
  .ant-select-selector {
    height: 34px !important;
    font-size: 14px !important;
  }
`;

const ValueFilter = ({
  onChange,
  needsConfirm,
  activeFilters = [],
  disabled,
  intl,
  isSorted,
  showCount = true,
  isColumnFilter,
  description,
  hasCategories,
  options = [],
  operators = [],
  info = '',
}) => {
  const [operator, setOperator] = useState(null);
  const [allOptions, setAllOptions] = useState(options);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [filterValue, setFilterValue] = useState('');
  const [selectedValues, setSelectedValues] = useState([]);

  useDeepCompareEffect(() => {
    if (operators?.length) {
      setOperator(searchOperators.matchAny.id);
    }
  }, [operators]);

  const getOperatorAndValue = () => {
    let operatorAndValue;
    let activeFiltersList = activeFilters;

    if (activeFilters.length !== 1) {
      activeFiltersList = activeFilters.join(';');
      operatorAndValue = determineOperatorAndValue(activeFiltersList);
    } else {
      operatorAndValue = determineOperatorAndValue(activeFiltersList[0]);
    }
    return operatorAndValue;
  };

  useDeepCompareEffect(() => {
    if (operators?.length && activeFilters[0]) {
      const operatorAndValue = getOperatorAndValue();
      setSelectedValues(operatorAndValue?.value?.split(';'));
      setOperator(operatorAndValue?.operator);
    } else {
      setSelectedValues(activeFilters);
    }
  }, [activeFilters]);

  useDeepCompareEffect(() => {
    setAllOptions(options);
  }, [options]);

  const onOptionChanged = ({ id, e }) => {
    if (needsConfirm) {
      setSelectedValues((s) => {
        if (!e.target.checked) {
          return s?.filter(v => v !== id);
        }
        return [...s, id];
      });
    } else {
      onChange({
        value: id,
        checked: e.target.checked,
        operator: operators?.find(o => o.id === operator),
      });
    }
  };

  const changeVisibility = () => {
    setIsCollapsed(s => !s);
  };

  const clearFilter = () => {
    setFilterValue('');
    setIsCollapsed(true);
  };

  const filterChanged = (e) => {
    const { value } = e.target;
    // When starting filtering, collapse open.
    if (value) {
      setFilterValue(value);
      setIsCollapsed(false);
    } else {
      setFilterValue(value);
      setIsCollapsed(true);
    }
  };

  const handleOperatorChange = (value) => {
    setOperator(value.value);
    if (activeFilters?.length) {
      onChange({
        operator: operators?.find(o => o.id === value.value),
      });
    }
  };

  const sortData = o => [...o]
    ?.sort((a, b) => {
      if (activeFilters.length) {
        const operatorAndValue = getOperatorAndValue();
        const activeFiltersSortList = operatorAndValue?.value?.split(';');

        // Make sure the active filters are on top.
        const includesForA = activeFiltersSortList?.includes(a?.id);
        const includesForB = activeFiltersSortList?.includes(b?.id);

        if (includesForB !== includesForA) {
          return includesForB - includesForA;
        }
      }

      if (!isNaN(b.sort) && !isNaN(a.sort)) {
        return b.sort - a.sort;
      }

      if (b.count === a.count) {
        // return alphabetically for same count
        return a.description.localeCompare(b.description);
      }
      return b.count - a.count;
    })
    ?.sort((a, b) => b.isDisabled - a.isDisabled)
    ?.filter((item) => {
      // Always show active filters, even when filtering
      if (activeFilters.includes(item.id)) {
        return true;
      }
      return item.description?.toLowerCase().includes(filterValue?.toLowerCase());
    })
    ?.filter((item, i) => {
      if (activeFilters.includes(item.id)) {
        return true;
      }
      if (isCollapsed) {
        return i <= 4;
      }
      return true;
    });

  const renderDataForFilter = (o = []) => {
    const sortedAndFilteredOptions = isSorted ? [...o] : sortData(o);

    return (
      <>
        {options.length <= 0 &&
          <StyledDiv>
            <FormattedMessage id='valueFilter.NO_FILTERS' />
          </StyledDiv>
        }
        {operator ?
          <StyledSelect
            options={operators?.map(op => ({
              value: op.id,
              label: op.description,
              shortHand: o.shortHand,
            }))}
            value={operator}
            onSelect={(e, op) => handleOperatorChange(op)}
            isColumnFilter={isColumnFilter}
          /> :
          null
        }
        {(options.length > 5 || operator) &&
          <StyledInput
            withOperator={!!operator}
            value={filterValue}
            onChange={filterChanged}
            placeholder={intl.formatMessage({ id: 'search.SEARCH' })}
            prefix={<Icon type='search' />}
            isColumnFilter={isColumnFilter}
            suffix={
              filterValue && <Icon type='times-circle' onClick={clearFilter} />
            }
          />
        }
        {sortedAndFilteredOptions?.map(op =>
          <div key={op.id}>
            <StyledCheckbox
              onChange={e => onOptionChanged({ id: op.id, e })}
              checked={selectedValues.includes(op.id) || op.isDisabled}
              disabled={disabled || op.isDisabled}
            >{`${op.description} ${op.count && showCount ? `(${op.count})` : ''}`}
            </StyledCheckbox>
          </div>,
        )}
        {options.length > 5 &&
          <HideLink onClick={changeVisibility}>
            <FormattedMessage id={`tickets.filters.${isCollapsed ? 'SHOW_ALL' : 'HIDE'}`} />
            <HideIcon type={`chevron-${isCollapsed ? 'down' : 'up'}`} />
          </HideLink>
        }
      </>
    );
  };

  if (hasCategories) {
    return (
      <>
        {!isColumnFilter && <Title>
          {description.toUpperCase()}
          <InfoTooltip content={info} />
        </Title>
        }
        {allOptions.length <= 0 &&
          <StyledDiv>
            <FormattedMessage id='valueFilter.NO_FILTERS' />
          </StyledDiv>
        }
        {allOptions &&
          allOptions.length > 0 &&
          allOptions?.map(category =>
            <>
              <Subtitle>{category.description}</Subtitle>
              <StyledWrapperCategory>
                {category.data && renderDataForFilter(category.data)}
              </StyledWrapperCategory>
            </>,
          )}
        {needsConfirm && <Action
          type='secondary'
          onClick={() => onChange(selectedValues, true)}
        >
          {intl.formatMessage({ id: 'worklist.filters.APPLY' })}
        </Action>
        }
      </>
    );
  }

  return (
    <>
      {!isColumnFilter && <Title>
        {description.toUpperCase()}
        <InfoTooltip content={info} />
      </Title>
      }
      {renderDataForFilter(allOptions)}
      {needsConfirm && <Action
        type='secondary'
        onClick={() => onChange({}, selectedValues)}
      >
        {intl.formatMessage({ id: 'worklist.filters.APPLY' })}
      </Action>
      }
    </>
  );
};

export default injectIntl(ValueFilter);

ValueFilter.propTypes = {
  intl: PropTypes.object,
  description: PropTypes.string,
  options: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  activeFilters: PropTypes.array,
  isSorted: PropTypes.bool,
  hasCategories: PropTypes.bool,
  showCount: PropTypes.bool,
  isColumnFilter: PropTypes.bool,
  needsConfirm: PropTypes.bool,
  operators: PropTypes.array,
  info: PropTypes.string,
};
